import {
  Box,
  BoxProps,
  Checkbox,
  Chip,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  SelectProps,
  TextField,
  Typography,
} from '@mui/material';
import classNames from 'classnames/bind';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownIcon, SearchLineIcon, TextSortAscIcon, TextSortDescIcon } from '~/assets/icons';
import { cast } from '~/utils/helper';
import BaseAutocomplete from '../base-autocomplete/BaseAutocomplete';
import styles from './BaseSelect.module.scss';

const cx = classNames.bind(styles);

export type BaseSelectProps<T extends Record<string, any>> = BoxProps &
  SelectProps & {
    value?: any;
    valueDisplay?: any;
    label?: string | undefined;
    placeholder?: string | undefined;
    renderSelected?: (item: T) => React.ReactElement;
    onChange?: (event: any) => void;
    options?: Array<T>;
    renderOptionItem?: (item: T, index: number) => React.ReactElement;
    menuClassName?: string;
    isSearchEnable?: boolean;
    isSortEnable?: boolean;
    isHideIcon?: boolean;
    bottomComponent?: () => React.ReactElement;
    topComponent?: () => React.ReactElement;
    helperText?: string;
    onSearchChange?: (...event: any[]) => void;
    typeOption?: string;
    selectedInOption?: boolean;
    onOpen?: (status: boolean) => void;
    isDisplayAllOption?: boolean;
    displayInvalidValue?: boolean; // In the case value doesn't match any options, display the value instead default
    showNumberOption?: number;
    enterValue?: boolean;
    disableClearable?: boolean;
    menuItemWidth?: number;
    selectAllOptions?: (data: any) => void;
    showIcon?: boolean;
  };
const BaseSelect = <T extends Record<string, any>>(props: BaseSelectProps<T>) => {
  //#region Destructuring Props
  const {
    isHideIcon = false,
    isSearchEnable = false,
    isSortEnable = true,
    value,
    label,
    placeholder,
    renderSelected,
    onChange,
    options,
    className,
    typeOption,
    isDisplayAllOption = false,
    ...restProps
  }: BaseSelectProps<T> = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('admin/component');
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [selectOptions, setSelectOptions] = useState<Array<T>>();
  const [searchValue, setSearchValue] = useState<string>('');
  const [selected, setSelected] = useState<any>();
  const [sort, setSort] = useState<string>('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const exists = options?.find((item) => item.id === value);
    setSelected(exists as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchValue]);
  const getFilterOption = useCallback((options: Array<T>, value: string) => {
    const dataFilter = options.filter((item) => {
      return value === '' || item.name?.toLowerCase().indexOf(value.toLowerCase()) > -1;
    });
    const dataAccurate = options.find((item) => item.name?.toLowerCase() === value?.toLowerCase());
    if (props.enterValue && value.trim() !== '' && !dataAccurate) {
      return [...[{ id: value, name: value }], ...dataFilter] as any;
    }
    return dataFilter;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (options !== undefined) {
      setSelectOptions(getFilterOption(options, searchValue));
    }
  }, [options, getFilterOption, searchValue]);
  //#endregion Implement Hook

  //#region Handle Function
  const renderOptionItem =
    props.renderOptionItem ||
    function (option: T, _index: number) {
      let isChecked = value === option.id;
      if (Array.isArray(value)) isChecked = value.indexOf(option.id) > -1;

      return (
        <Box key={option.id} className='select-option w-full flex'>
          {restProps.multiple && <Checkbox checked={isChecked} className='mr-[7px]' />}
          <Typography className='text-center flex-1 text-base'>{option.name}</Typography>
        </Box>
      );
    };

  const renderSelectedItem =
    renderSelected ||
    function (selectedItem) {
      if (typeOption === 'date') {
        const date = new Date(selectedItem.id).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        return <Box className='p-[15px]'>{date}</Box>;
      }
      if (Array.isArray(selectedItem)) {
        const selectedComponent = selectedItem.map((item) => {
          return <Chip key={item.id} label={item.name} variant='outlined' size='small' className='mr-[5px]' />;
        });
        return <Box className='p-[15px]'>{selectedComponent}</Box>;
      }
      return <Box className='p-3.5 text-base leading-5 truncate text-black'>{selectedItem.name}</Box>;
    };

  const onSearch = (event: React.ChangeEvent) => {
    const value = (event.target as HTMLInputElement).value;
    setSearchValue(value);
    if (options?.length) {
      setSelectOptions(getFilterOption(options, value));
    }
  };
  //#endregion Handle Function

  // Remove invalid attribute
  delete restProps.menuClassName;
  delete restProps.renderOptionItem;
  delete restProps.topComponent;
  delete restProps.bottomComponent;
  delete restProps.helperText;
  delete restProps.onSearchChange;

  return (
    <>
      {!props.enterValue && (
        <Box className={cx('input-select-container', className)}>
          {selectOptions && (
            <Select
              value={value}
              onChange={props.onChange}
              onOpen={() => {
                props.onOpen && props.onOpen(true);
              }}
              renderValue={() => {
                if (value !== undefined) {
                  let selectedItem: any = null;
                  if (value === '' || (Array.isArray(value) && !value.length)) {
                    return (
                      <Box className='pl-[15px] pr-[25px] text-[#7C8796] text-base font-normal truncate'>
                        {placeholder}
                      </Box>
                    );
                  }
                  if (Array.isArray(value)) {
                    selectedItem = [];
                    for (let i in value) {
                      let matchedOption = props.options?.find((item) => item.id === value[i]);
                      if (matchedOption) selectedItem.push(matchedOption);
                    }
                  } else {
                    selectedItem = props.options?.find((item) => item.id === value);
                  }

                  return selectedItem ? (
                    renderSelectedItem(selectedItem)
                  ) : props.displayInvalidValue || props.enterValue ? (
                    props.valueDisplay ? (
                      <Box className='pl-[15px] pr-[25px] truncate'>{props.valueDisplay}</Box>
                    ) : (
                      <Box className='pl-[15px] pr-[25px] truncate'>{value}</Box>
                    )
                  ) : (
                    <Box className='pl-[15px] pr-[25px] text-[#7C8796] text-base font-normal truncate'>
                      {placeholder ?? label}
                    </Box>
                  );
                }

                return (
                  <Box className='flex px-[15px] text-base font-normal flex-1 select-none text-[#7C8796]'>
                    {placeholder ?? label ?? ''}
                  </Box>
                );
              }}
              defaultValue={props.defaultValue}
              inputProps={{ 'aria-label': 'Without label' }}
              displayEmpty={true}
              IconComponent={
                isHideIcon
                  ? 'span'
                  : (props) => <ArrowDownIcon stroke='#7C8796' {...props} className={cx('dropdown-select-icon')} />
              }
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'right',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'right',
                },
                id: 'select-menu',
                sx: { minWidth: 220 },
                className: cx('base-select-menu-item', props?.menuClassName),
              }}
              {...restProps}
            >
              <Box>
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    background: '#fff',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {label && (
                    <Typography className='mb-[16px] text-[14px]/[16.8px] text-[#3A3F51] font-medium'>
                      {label}
                    </Typography>
                  )}
                  {(isSearchEnable || props.enterValue) && (
                    <Box>
                      <Box className='flex justify-between gap-2'>
                        <TextField
                          onKeyDown={(e) => e.stopPropagation()}
                          className={isSortEnable ? 'mb-[9px] sticky flex-grow' : 'mb-[9px] sticky w-full'}
                          onChange={props.onSearchChange ? props.onSearchChange : onSearch}
                          defaultValue={searchValue}
                          InputProps={{ endAdornment: <SearchLineIcon fill='#005F73' /> }}
                        />
                        {isSortEnable ? (
                          <IconButton
                            className='w-[35px] h-[35px]'
                            onClick={() => {
                              if (!sort || sort === 'DESC') {
                                setSort('ASC');
                              } else {
                                setSort('DESC');
                              }
                            }}
                          >
                            {!sort || sort === 'ASC' ? (
                              <TextSortAscIcon fill='#005f73' />
                            ) : (
                              <TextSortDescIcon fill='#005f73' />
                            )}
                          </IconButton>
                        ) : null}
                      </Box>
                    </Box>
                  )}
                  {restProps.multiple && (
                    <Box>
                      <Box className='flex justify-between mb-2.5'>
                        <Typography
                          className='underline text-[#ED1002] cursor-pointer'
                          onClick={() => {
                            if (restProps?.selectAllOptions && options?.length) {
                              restProps?.selectAllOptions(options?.map((item) => item.id));
                            }
                          }}
                        >
                          {t('baseSelect.selectAll')} ({options?.length})
                        </Typography>
                        <Typography
                          className='underline text-[#ED1002] cursor-pointer'
                          onClick={() => {
                            if (restProps?.selectAllOptions) {
                              restProps?.selectAllOptions([]);
                            }
                          }}
                        >
                          {t('baseSelect.delete')}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              {props.topComponent && props.topComponent()}
              <MenuItem className={isDisplayAllOption ? '' : 'hidden'} value=''>
                <Typography className='text-center w-full text-base leading-8'>{t('baseSelect.select')}</Typography>
              </MenuItem>
              <MenuItem key={value + cast(selected?.name)} value={value + cast(selected?.name)}></MenuItem>
              {selectOptions && renderOptionItem
                ? (props.showNumberOption ? selectOptions.slice(0, props.showNumberOption) : selectOptions)
                    .slice()
                    .sort((a, b) => {
                      if (sort === 'ASC') {
                        return a.name.localeCompare(b.name);
                      }
                      if (sort === 'DESC') {
                        return b.name.localeCompare(a.name);
                      }
                      return 0;
                    })
                    .map((option: T, index: number) => {
                      if (typeOption === 'eventNumber' && props.selectedInOption === false && option.id === value) {
                        return;
                      }
                      return (
                        <MenuItem
                          key={index}
                          value={option.id}
                          className='mb-1'
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: '#F3F6F9',
                            },
                            '&.Mui-selected .MuiTypography-root ': {
                              color: '#161A1F',
                              fontSize: 16,
                              fontWeight: 500,
                            },
                          }}
                        >
                          {renderOptionItem(option, index)}
                        </MenuItem>
                      );
                    })
                : restProps.children}
              {props.bottomComponent && props.bottomComponent()}
            </Select>
          )}
        </Box>
      )}
      {props.enterValue && <BaseAutocomplete {...(props as any)} />}
      {props.helperText && (
        <Box>
          <FormHelperText error={true} className='text-xs'>
            {props.helperText}
          </FormHelperText>
        </Box>
      )}
    </>
  );
};

export default memo(BaseSelect) as typeof BaseSelect;
